/**
 * Declarative nav + route surface for every role shell.
 *
 * GENERATED from App.tsx's six role shells by scripts/generate_shell_config.py,
 * then committed. Phase 3 Package 1 (.claude/specs/module-ui-rollout.md).
 *
 * Why generated: the six shells held 188 nav entries and 191 routes between them.
 * Retyping that is an attrition task, and attrition is how a route quietly goes
 * missing. scripts/extract_config_surface.py proves this file reproduces the old
 * surface exactly, guards and section dividers included.
 *
 * A function rather than a constant, because nav labels reference live dashboard
 * counts and routes reference components — neither exists at module scope.
 *
 * Guard strings are evaluated by ModuleShell's guardHolds(). The token set is
 * closed — an unrecognised token throws in dev and fails closed in prod:
 *   "role:admin"      -> user?.role === "admin" ("role:admin,hr" is membership)
 *   "timesheetEnabled" -> tenant has the Timesheet setting on
 *   "legacyInvoicing"  -> tenant is grandfathered into legacy Invoices / CD Notes
 * Conditions join with "+" and all must hold.
 */
import React from "react";
import { Navigate } from "react-router-dom";

import { NotFound, EmployeeSlipsView, NavBadge, AccessDenied, OffersPageWrapper } from "./components/shellPrimitives";
import AIExpenseCapturePage from "./pages/AIExpenseCapturePage";
import AnnouncementsPage from "./pages/AnnouncementsPage";
import AssetsPage from "./pages/AssetsPage";
import AttendancePage from "./pages/AttendancePage";
import PeopleSettingsPage from "./pages/PeopleSettingsPage";
import BillingNotesPage from "./pages/BillingNotesPage";
import BulkReceiptUploadPage from "./pages/BulkReceiptUploadPage";
import CandidatesPage from "./pages/CandidatesPage";
import DashboardPage from "./pages/DashboardPage";
import DebitCreditNotesPage from "./pages/DebitCreditNotesPage";
import EmployeeDashboard from "./pages/EmployeeDashboard";
import EmployeePortal from "./pages/EmployeePortal";
import EmployeesPage from "./pages/EmployeesPage";
import EsignPage from "./pages/EsignPage";
import EsignSignPage from "./pages/EsignSignPage";
import ExitPage from "./pages/ExitPage";
import ExpensesPage from "./pages/ExpensesPage";
import HelpdeskPage from "./pages/HelpdeskPage";
import InterviewHubPage from "./pages/InterviewHubPage";
import InvoicesPage from "./pages/InvoicesPage";
import JobDetailPage from "./pages/JobDetailPage";
import JobsPage from "./pages/JobsPage";
import LeaveManagementPage from "./pages/LeaveManagementPage";
import LettersPage from "./pages/LettersPage";
import NewJobPage from "./pages/NewJobPage";
import OrgChartPage from "./pages/OrgChartPage";
import PerformancePage from "./pages/PerformancePage";
import PoliciesPage from "./pages/PoliciesPage";
import ProductsPage from "./pages/ProductsPage";
import PulsePage from "./pages/PulsePage";
import QuotationsPage from "./pages/QuotationsPage";
import ReceiptsPage from "./pages/ReceiptsPage";
import RecruiterPreferencesPage from "./pages/RecruiterPreferencesPage";
import ReferralsPage from "./pages/ReferralsPage";
import PeopleReportsPage from "./pages/PeopleReportsPage";
import RecruitmentReportsPage from "./pages/RecruitmentReportsPage";
import PayrollReportsPage from "./pages/PayrollReportsPage";
import SalarySlipsPage from "./pages/SalarySlipsPage";
import SalesCreditDebitNotesPage from "./pages/SalesCreditDebitNotesPage";
import ScreeningPage from "./pages/ScreeningPage";
import SecurityPage from "./pages/SecurityPage";
import SettingsPage from "./pages/SettingsPage";
import TalentPoolPage from "./pages/TalentPoolPage";
import TaxInvoicesPage from "./pages/TaxInvoicesPage";
import TenantPage from "./pages/TenantPage";
import TimesheetPage from "./pages/TimesheetPage";
import VendorBillsPage from "./pages/VendorBillsPage";
import VendorsPage from "./pages/VendorsPage";

/** A single guard condition, as guardHolds() in App.tsx resolves it.
 *
 *  This is `string` no longer, because `string` is how a retired token survives.
 *  #398 closed the runtime token set (spec feature-access-removal) while #397 was
 *  adding `guard: "salary_slips"` in another branch; both merged clean, and from
 *  then on guardHolds failed the dead token closed and silently unmounted every
 *  link and route carrying it. A union makes that a compile error instead.
 *
 *  Widening this is deliberate: add the arm here AND the branch in guardHolds. */
export type GuardAtom = `role:${string}` | "timesheetEnabled" | "legacyInvoicing";

/** "a+b" means both conditions must hold. Two atoms is all any guard has needed. */
export type Guard = GuardAtom | `${GuardAtom}+${GuardAtom}`;

export type NavEntry =
  | { section: string; guard?: Guard }
  | { to: string; label: React.ReactNode; guard?: Guard; end?: boolean };

export interface RouteEntry {
  path: string;
  // ReactElement, not ReactNode: react-router's RouteProps.element is
  // ReactElement | null, and ReactNode is wider than that.
  element: React.ReactElement;
  guard?: Guard;
}

export interface ShellDef {
  nav: NavEntry[];
  routes: RouteEntry[];
}

export type RoleKey =
  | "employee" | "contractor" | "admin_assistant"
  | "finance" | "recruiter" | "hr";

export interface ShellCtx {
  user: any;
  timesheetEnabled: boolean;
  legacyInvoicingEnabled: boolean;
  summary: any;
}

// ── Module sub-page fragments (spec module-subpage-navs) ─────────────────────
// One canonical definition per module, spread into every role that has the
// module. A role can widen/narrow only by choosing which fragment it spreads
// (self vs manage, sender vs signer) — never by retyping the list, so the
// sub-page surface cannot silently drift between roles (AC5).

const PAYROLL_NAV = (guard?: Guard): NavEntry[] => [
  { to: "/payroll", label: "Payslips", end: true, guard },
  { to: "/payroll/structure", label: "Salary Structure", guard },
  { to: "/payroll/settings", label: "Payroll Settings", guard },
  { to: "/payroll/exports", label: "Exports", guard },
];
const PAYROLL_SUBROUTES = (element: React.ReactElement, guard?: Guard): RouteEntry[] =>
  ["structure", "settings", "exports"].map((seg) => ({ path: `/payroll/${seg}`, element, guard }));

// Self-serve areas every Performance user gets; Manage areas mirror the page's
// own isAdmin gate (admin / admin_assistant / hr).
const PERFORMANCE_NAV_SELF = (guard?: Guard): NavEntry[] => [
  { to: "/performance/goals", label: "My Goals", guard },
  { to: "/performance", label: "My Reviews", end: true, guard },
  { to: "/performance/check-ins", label: "Check-ins", guard },
];
const PERFORMANCE_NAV_MANAGE = (guard?: Guard): NavEntry[] => [
  { to: "/performance/cycles", label: "Manage Cycles", guard },
  { to: "/performance/analytics", label: "Analytics", guard },
];
const PERFORMANCE_SUBROUTES_SELF = (element: React.ReactElement, guard?: Guard): RouteEntry[] =>
  ["goals", "check-ins"].map((seg) => ({ path: `/performance/${seg}`, element, guard }));
const PERFORMANCE_SUBROUTES_MANAGE = (element: React.ReactElement, guard?: Guard): RouteEntry[] =>
  ["cycles", "analytics"].map((seg) => ({ path: `/performance/${seg}`, element, guard }));

// Sender fragment mirrors EsignPage's SENDER_ROLES (admin, admin_assistant,
// hr, recruiter); everyone else is a signer and gets exactly the To Sign
// surface they see today.
const ESIGN_NAV_SENDER = (guard?: Guard): NavEntry[] => [
  { to: "/esign", label: "Send for Signature", end: true, guard },
  { to: "/esign/templates", label: "Templates", guard },
  { to: "/esign/sent", label: "Sent Documents", guard },
  { to: "/esign/to-sign", label: "To Sign", guard },
];
const ESIGN_NAV_SIGNER = (guard?: Guard): NavEntry[] => [
  { to: "/esign", label: "To Sign", end: true, guard },
];
const ESIGN_SUBROUTES_SENDER = (element: React.ReactElement, guard?: Guard): RouteEntry[] =>
  ["templates", "sent", "to-sign"].map((seg) => ({ path: `/esign/${seg}`, element, guard }));
// Signers keep /esign/to-sign resolvable so a deep link shared by a sender
// degrades to their To Sign view rather than a 404.
const ESIGN_SUBROUTES_SIGNER = (element: React.ReactElement, guard?: Guard): RouteEntry[] =>
  [{ path: "/esign/to-sign", element, guard }];

const AIEXP_NAV = (guard?: Guard): NavEntry[] => [
  { to: "/ai-expense-capture", label: "Captures", end: true, guard },
  { to: "/ai-expense-capture/ledger", label: "Ledger", guard },
  { to: "/ai-expense-capture/branches", label: "Branches", guard },
  { to: "/ai-expense-capture/insights", label: "Insights", guard },
  // Named for its module, matching Payroll Settings. A bare "Settings" sat two
  // rows above the System "Settings" and read as a duplicate of it.
  { to: "/ai-expense-capture/settings", label: "AI Expense Settings", guard },
];
const AIEXP_SUBROUTES = (element: React.ReactElement, guard?: Guard): RouteEntry[] =>
  ["ledger", "branches", "insights", "settings"].map((seg) => ({ path: `/ai-expense-capture/${seg}`, element, guard }));

export function buildShellConfig(ctx: ShellCtx): Record<RoleKey, ShellDef> {
  const { user, timesheetEnabled, legacyInvoicingEnabled, summary } = ctx;
  void user; void timesheetEnabled; void legacyInvoicingEnabled; void summary;
  return {
    // employee (was EmployeeShell)
    employee: {
      nav: [
        { to: "/", label: "Dashboard", end: true },
        { to: "/announcements", label: "Announcements" },
        { to: "/my-leaves", label: "My Leaves" },
        { to: "/attendance", label: "Attendance" },
        { to: "/timesheet", label: "Timesheet", guard: "timesheetEnabled" },
        { to: "/my-slips", label: "Salary Slips" },
        { to: "/expenses", label: "Reimbursements" },
        ...PERFORMANCE_NAV_SELF(),
        { to: "/assets", label: "Assets" },
        { to: "/letters", label: "Letters" },
        ...ESIGN_NAV_SIGNER(),
        { to: "/helpdesk", label: "Helpdesk" },
        { to: "/referrals", label: "Refer a Friend" },
        { to: "/pulse", label: "Pulse Survey" },
        { to: "/policies", label: "Policies" },
        { to: "/exit", label: "Offboarding" },
        // No "Reports" entry for non-manager roles: the only report they ever
        // had was their own leave utilization, which now lives on My Leaves
        // where they already go to look at their balance (Package 1, AC3).
        { to: "/security", label: "Security" },
      ],
      routes: [
        { path: "/", element: <EmployeeDashboard /> },
        { path: "/announcements", element: <AnnouncementsPage /> },
        { path: "/my-leaves", element: <EmployeePortal /> },
        { path: "/attendance", element: <AttendancePage /> },
        { path: "/timesheet", element: <TimesheetPage />, guard: "timesheetEnabled" },
        { path: "/my-slips", element: <EmployeeSlipsView /> },
        { path: "/expenses", element: <ExpensesPage /> },
        { path: "/performance", element: <PerformancePage /> },
        ...PERFORMANCE_SUBROUTES_SELF(<PerformancePage />),
        { path: "/assets", element: <AssetsPage /> },
        { path: "/letters", element: <LettersPage /> },
        { path: "/esign", element: <EsignPage /> },
        ...ESIGN_SUBROUTES_SIGNER(<EsignPage />),
        { path: "/esign/:requestId/:recipientId", element: <EsignSignPage /> },
        { path: "/helpdesk", element: <HelpdeskPage /> },
        { path: "/referrals", element: <ReferralsPage /> },
        { path: "/pulse", element: <PulsePage /> },
        { path: "/policies", element: <PoliciesPage /> },
        { path: "/exit", element: <ExitPage /> },
        // AC2/AC3 — the self-service report moved onto My Leaves; the old route
        // redirects there rather than 404ing.
        { path: "/reports", element: <Navigate to="/my-leaves" replace /> },
        { path: "/security", element: <SecurityPage /> },
        { path: "*", element: <NotFound /> },
      ],
    },
    // contractor (was ContractorShell)
    contractor: {
      nav: [
        { to: "/", label: "Dashboard", end: true },
        { to: "/announcements", label: "Announcements" },
        { to: "/timesheet", label: "Timesheet" },
        { to: "/attendance", label: "Attendance" },
        { to: "/my-leaves", label: "My Leaves" },
        { to: "/expenses", label: "Reimbursements" },
        { to: "/assets", label: "Assets" },
        { to: "/letters", label: "Letters" },
        ...ESIGN_NAV_SIGNER(),
        { to: "/helpdesk", label: "Helpdesk" },
        // No "Reports" entry for non-manager roles: the only report they ever
        // had was their own leave utilization, which now lives on My Leaves
        // where they already go to look at their balance (Package 1, AC3).
        { to: "/security", label: "Security" },
      ],
      routes: [
        { path: "/", element: <EmployeeDashboard /> },
        { path: "/announcements", element: <AnnouncementsPage /> },
        { path: "/timesheet", element: <TimesheetPage /> },
        { path: "/attendance", element: <AttendancePage /> },
        { path: "/my-leaves", element: <EmployeePortal /> },
        { path: "/expenses", element: <ExpensesPage /> },
        { path: "/assets", element: <AssetsPage /> },
        { path: "/letters", element: <LettersPage /> },
        { path: "/esign", element: <EsignPage /> },
        ...ESIGN_SUBROUTES_SIGNER(<EsignPage />),
        { path: "/esign/:requestId/:recipientId", element: <EsignSignPage /> },
        { path: "/helpdesk", element: <HelpdeskPage /> },
        // AC2/AC3 — the self-service report moved onto My Leaves; the old route
        // redirects there rather than 404ing.
        { path: "/reports", element: <Navigate to="/my-leaves" replace /> },
        { path: "/security", element: <SecurityPage /> },
        { path: "*", element: <NotFound /> },
      ],
    },
    // admin_assistant (was AdminAssistantShell)
    admin_assistant: {
      nav: [
        { to: "/", label: "Dashboard", end: true },
        { section: "Recruitment" },
        { to: "/jobs", label: <NavBadge label="Jobs" count={summary?.open_jobs} color="#6366f1" /> },
        { to: "/candidates", label: "Candidates" },
        { to: "/talent-pool", label: "Talent Pool" },
        { to: "/offers", label: "Offers" },
        { to: "/interview-hub", label: "Interview Hub" },
        { to: "/screening", label: "AI Screening" },
        { to: "/recruitment-reports", label: "Reports" },
        { to: "/recruiter-preferences", label: "Recruiter Preferences" },
        { section: "People" },
        { to: "/leaves", label: <NavBadge label="Leaves" count={summary?.pending_leaves} /> },
        { to: "/my-leaves", label: "My Leaves" },
        { to: "/my-slips", label: "My Salary Slips" },
        { to: "/employees", label: "Employees" },
        { to: "/attendance", label: "Attendance" },
        { to: "/timesheet", label: "Timesheet", guard: "timesheetEnabled" },
        { to: "/people-settings", label: "People Settings" },
        ...PERFORMANCE_NAV_SELF(),
        ...PERFORMANCE_NAV_MANAGE(),
        { to: "/org-chart", label: "Org Chart" },
        { to: "/expenses", label: "Reimbursements" },
        { to: "/assets", label: "Assets" },
        { to: "/letters", label: "Letters" },
        ...ESIGN_NAV_SENDER(),
        { to: "/helpdesk", label: "Helpdesk" },
        { to: "/announcements", label: "Announcements" },
        { to: "/referrals", label: "Referrals" },
        { to: "/pulse", label: "Pulse" },
        { to: "/policies", label: "Policies" },
        { to: "/exit", label: "Offboarding" },
        // Reports fold into the module that owns their data (spec
        // reports-v2-shell-and-people-analytics, Package 1). Payroll Reports is
        // listed here rather than appended to PAYROLL_NAV because that nav is
        // shared with the finance shell, and the report API is
        // require_leave_manager (admin/admin_assistant/hr) — finance would get
        // a visible link that 403s.
        { to: "/people-reports", label: "Reports" },
        // One section per purchasable module. Payroll, Finance and AI Expense
        // Capture are three separate entries in MODULE_KEYS, and a tenant can
        // own any one without the others — so filing all three under a single
        // "Finance" heading described the org chart rather than what the
        // workspace actually has.
        { section: "Payroll" },
        ...PAYROLL_NAV(),
        // Sits with the payroll nav it reports on, not up in People. Still
        // listed separately rather than appended to PAYROLL_NAV, because that
        // fragment is shared with the finance shell and this report is
        // require_leave_manager (admin/admin_assistant/hr) — finance would get
        // a visible link that 403s.
        { to: "/payroll/reports", label: "Reports" },
        { section: "Finance" },
        { to: "/invoices", label: <NavBadge label="Invoices" count={summary?.overdue_invoices} color="#ef4444" />, guard: "legacyInvoicing" },
        { to: "/quotations", label: "Quotations" },
        { to: "/billing-notes", label: "Billing Notes" },
        { to: "/tax-invoices", label: "Tax Invoices" },
        { to: "/receipts", label: "Receipts" },
        { to: "/debit-credit-notes", label: "Credit / Debit Notes (Legacy)", guard: "legacyInvoicing" },
        { to: "/sales-credit-debit-notes", label: "Credit / Debit Notes" },
        { section: "AI Expense Capture" },
        ...AIEXP_NAV(),
        { to: "/products", label: "Products" },
        { to: "/vendors", label: "Vendors" },
        { to: "/vendor-bills", label: "Vendor Bills" },
        { section: "System" },
        { to: "/settings", label: "Settings", end: true },
        { to: "/workspace", label: "Workspace" },
        { to: "/security", label: "Security" },
      ],
      routes: [
        { path: "/", element: <DashboardPage /> },
        { path: "/jobs", element: <JobsPage /> },
        { path: "/jobs/new", element: <NewJobPage /> },
        { path: "/jobs/:id", element: <JobDetailPage /> },
        { path: "/candidates", element: <CandidatesPage /> },
        { path: "/talent-pool", element: <TalentPoolPage /> },
        { path: "/offers", element: <OffersPageWrapper /> },
        { path: "/screening", element: <ScreeningPage /> },
        { path: "/interview-hub", element: <InterviewHubPage /> },
        { path: "/recruitment-reports", element: <RecruitmentReportsPage /> },
        { path: "/recruitment-reports/time-to-hire", element: <RecruitmentReportsPage /> },
        { path: "/recruitment-reports/sources", element: <RecruitmentReportsPage /> },
        { path: "/recruitment-reports/interviews", element: <RecruitmentReportsPage /> },
        { path: "/recruitment-reports/offers", element: <RecruitmentReportsPage /> },
        { path: "/recruitment-reports/pipeline", element: <RecruitmentReportsPage /> },
        { path: "/recruitment-reports/screening", element: <RecruitmentReportsPage /> },
        { path: "/recruiter-preferences", element: <RecruiterPreferencesPage /> },
        { path: "/preferred-companies", element: <Navigate to="/recruiter-preferences" replace /> },
        { path: "/leaves", element: <LeaveManagementPage /> },
        { path: "/my-leaves", element: <EmployeePortal /> },
        { path: "/my-slips", element: <EmployeeSlipsView /> },
        { path: "/employees", element: <EmployeesPage /> },
        { path: "/attendance", element: <AttendancePage /> },
        { path: "/timesheet", element: timesheetEnabled ? <TimesheetPage /> : <NotFound />, guard: "timesheetEnabled" },
        { path: "/people-settings", element: <PeopleSettingsPage /> },
        { path: "/performance", element: <PerformancePage /> },
        ...PERFORMANCE_SUBROUTES_SELF(<PerformancePage />),
        ...PERFORMANCE_SUBROUTES_MANAGE(<PerformancePage />),
        { path: "/org-chart", element: <OrgChartPage /> },
        { path: "/expenses", element: <ExpensesPage /> },
        { path: "/assets", element: <AssetsPage /> },
        { path: "/letters", element: <LettersPage /> },
        { path: "/esign", element: <EsignPage /> },
        ...ESIGN_SUBROUTES_SENDER(<EsignPage />),
        { path: "/esign/:requestId/:recipientId", element: <EsignSignPage /> },
        { path: "/helpdesk", element: <HelpdeskPage /> },
        { path: "/referrals", element: <ReferralsPage /> },
        { path: "/pulse", element: <PulsePage /> },
        { path: "/announcements", element: <AnnouncementsPage /> },
        { path: "/policies", element: <PoliciesPage /> },
        { path: "/exit", element: <ExitPage /> },
        { path: "/people-reports", element: <PeopleReportsPage /> },
        { path: "/people-reports/movement", element: <PeopleReportsPage /> },
        { path: "/people-reports/attrition", element: <PeopleReportsPage /> },
        { path: "/people-reports/org", element: <PeopleReportsPage /> },
        { path: "/people-reports/leave", element: <PeopleReportsPage /> },
        { path: "/people-reports/liability", element: <PeopleReportsPage /> },
        { path: "/people-reports/absence", element: <PeopleReportsPage /> },
        { path: "/payroll/reports", element: <PayrollReportsPage /> },
        // AC2 — /reports is retired but must not 404 for anyone holding a
        // bookmark or a link in an old email.
        { path: "/reports", element: <Navigate to="/people-reports" replace /> },
        { path: "/payroll", element: <SalarySlipsPage /> },
        ...PAYROLL_SUBROUTES(<SalarySlipsPage />),
        { path: "/invoices", element: <InvoicesPage />, guard: "legacyInvoicing" },
        { path: "/quotations", element: <QuotationsPage /> },
        { path: "/billing-notes", element: <BillingNotesPage /> },
        { path: "/tax-invoices", element: <TaxInvoicesPage /> },
        { path: "/receipts", element: <ReceiptsPage /> },
        { path: "/debit-credit-notes", element: <DebitCreditNotesPage />, guard: "legacyInvoicing" },
        { path: "/sales-credit-debit-notes", element: <SalesCreditDebitNotesPage /> },
        { path: "/vendors", element: <VendorsPage /> },
        { path: "/vendor-bills", element: <VendorBillsPage /> },
        { path: "/vendor-bills/bulk-upload", element: <BulkReceiptUploadPage /> },
        { path: "/ai-expense-capture", element: <AIExpenseCapturePage /> },
        ...AIEXP_SUBROUTES(<AIExpenseCapturePage />),
        { path: "/products", element: <ProductsPage /> },
        { path: "/settings", element: <SettingsPage /> },
        { path: "/workspace", element: <TenantPage /> },
        { path: "/security", element: <SecurityPage /> },
        { path: "*", element: <NotFound /> },
      ],
    },
    // finance (was FinanceShell)
    finance: {
      nav: [
        { to: "/", label: "Dashboard", end: true },
        // One section per purchasable module. Payroll, Finance and AI Expense
        // Capture are three separate entries in MODULE_KEYS, and a tenant can
        // own any one without the others — so filing all three under a single
        // "Finance" heading described the org chart rather than what the
        // workspace actually has.
        { section: "Payroll" },
        ...PAYROLL_NAV(),
        { section: "Finance" },
        { to: "/invoices", label: <NavBadge label="Invoices" count={summary?.overdue_invoices} color="#ef4444" />, guard: "legacyInvoicing" },
        { to: "/quotations", label: "Quotations" },
        { to: "/billing-notes", label: "Billing Notes" },
        { to: "/tax-invoices", label: "Tax Invoices" },
        { to: "/receipts", label: "Receipts" },
        { to: "/debit-credit-notes", label: "Credit / Debit Notes (Legacy)", guard: "legacyInvoicing" },
        { to: "/sales-credit-debit-notes", label: "Credit / Debit Notes" },
        { section: "AI Expense Capture" },
        ...AIEXP_NAV(),
        { to: "/products", label: "Products" },
        { to: "/vendors", label: "Vendors" },
        { to: "/vendor-bills", label: "Vendor Bills" },
        { section: "My Work" },
        { to: "/my-leaves", label: "My Leaves" },
        { to: "/attendance", label: "Attendance" },
        { to: "/timesheet", label: "Timesheet", guard: "timesheetEnabled" },
        { to: "/my-slips", label: "Salary Slips" },
        { to: "/expenses", label: "Reimbursements" },
        { to: "/assets", label: "Assets" },
        { to: "/letters", label: "Letters" },
        ...ESIGN_NAV_SIGNER(),
        { to: "/helpdesk", label: "Helpdesk" },
        { to: "/referrals", label: "Refer a Friend" },
        { to: "/pulse", label: "Pulse Survey" },
        { to: "/announcements", label: "Announcements" },
        { to: "/policies", label: "Policies" },
        { to: "/exit", label: "Offboarding" },
        // See above — self-service leave utilization moved to My Leaves.
        { section: "System" },
        { to: "/security", label: "Security" },
      ],
      routes: [
        { path: "/", element: <DashboardPage /> },
        { path: "/payroll", element: <SalarySlipsPage /> },
        ...PAYROLL_SUBROUTES(<SalarySlipsPage />),
        { path: "/invoices", element: <InvoicesPage />, guard: "legacyInvoicing" },
        { path: "/quotations", element: <QuotationsPage /> },
        { path: "/billing-notes", element: <BillingNotesPage /> },
        { path: "/tax-invoices", element: <TaxInvoicesPage /> },
        { path: "/receipts", element: <ReceiptsPage /> },
        { path: "/debit-credit-notes", element: <DebitCreditNotesPage />, guard: "legacyInvoicing" },
        { path: "/sales-credit-debit-notes", element: <SalesCreditDebitNotesPage /> },
        { path: "/vendors", element: <VendorsPage /> },
        { path: "/vendor-bills", element: <VendorBillsPage /> },
        { path: "/vendor-bills/bulk-upload", element: <BulkReceiptUploadPage /> },
        { path: "/ai-expense-capture", element: <AIExpenseCapturePage /> },
        ...AIEXP_SUBROUTES(<AIExpenseCapturePage />),
        { path: "/products", element: <ProductsPage /> },
        { path: "/my-leaves", element: <EmployeePortal /> },
        { path: "/attendance", element: <AttendancePage /> },
        { path: "/timesheet", element: <TimesheetPage />, guard: "timesheetEnabled" },
        { path: "/my-slips", element: <EmployeeSlipsView /> },
        { path: "/expenses", element: <ExpensesPage /> },
        { path: "/assets", element: <AssetsPage /> },
        { path: "/letters", element: <LettersPage /> },
        { path: "/esign", element: <EsignPage /> },
        ...ESIGN_SUBROUTES_SIGNER(<EsignPage />),
        { path: "/esign/:requestId/:recipientId", element: <EsignSignPage /> },
        { path: "/helpdesk", element: <HelpdeskPage /> },
        { path: "/referrals", element: <ReferralsPage /> },
        { path: "/pulse", element: <PulsePage /> },
        { path: "/announcements", element: <AnnouncementsPage /> },
        { path: "/policies", element: <PoliciesPage /> },
        { path: "/exit", element: <ExitPage /> },
        // AC2/AC3 — the self-service report moved onto My Leaves; the old route
        // redirects there rather than 404ing.
        { path: "/reports", element: <Navigate to="/my-leaves" replace /> },
        { path: "/security", element: <SecurityPage /> },
        { path: "*", element: <NotFound /> },
      ],
    },
    // recruiter (was RecruiterShell)
    recruiter: {
      nav: [
        { to: "/", label: "Dashboard", end: true },
        { section: "Recruitment" },
        { to: "/jobs", label: <NavBadge label="Jobs" count={summary?.open_jobs} /> },
        { to: "/candidates", label: <NavBadge label="Candidates" count={summary?.new_candidates} color="#7c3aed" /> },
        { to: "/talent-pool", label: "Talent Pool" },
        { to: "/offers", label: "Offers" },
        { to: "/interview-hub", label: "Interview Hub" },
        { to: "/screening", label: "AI Screening" },
        { to: "/recruitment-reports", label: "Reports" },
        { to: "/recruiter-preferences", label: "Recruiter Preferences" },
        { section: "Personal" },
        { to: "/announcements", label: "Announcements" },
        { to: "/my-leaves", label: "My Leaves" },
        { to: "/attendance", label: "Attendance" },
        { to: "/timesheet", label: "Timesheet", guard: "timesheetEnabled" },
        { to: "/my-slips", label: "Salary Slips" },
        { to: "/expenses", label: "Reimbursements" },
        ...PERFORMANCE_NAV_SELF(),
        { to: "/assets", label: "Assets" },
        { to: "/letters", label: "Letters" },
        ...ESIGN_NAV_SENDER(),
        { to: "/helpdesk", label: "Helpdesk" },
        { to: "/referrals", label: "Refer a Friend" },
        { to: "/pulse", label: "Pulse Survey" },
        { to: "/policies", label: "Policies" },
        { to: "/exit", label: "Offboarding" },
        // No "Reports" entry for non-manager roles: the only report they ever
        // had was their own leave utilization, which now lives on My Leaves
        // where they already go to look at their balance (Package 1, AC3).
        { to: "/security", label: "Security" },
      ],
      routes: [
        { path: "/", element: <DashboardPage /> },
        { path: "/jobs", element: <JobsPage /> },
        { path: "/jobs/new", element: <NewJobPage /> },
        { path: "/jobs/:id", element: <JobDetailPage /> },
        { path: "/candidates", element: <CandidatesPage /> },
        { path: "/talent-pool", element: <TalentPoolPage /> },
        { path: "/offers", element: <OffersPageWrapper /> },
        { path: "/screening", element: <ScreeningPage /> },
        { path: "/interview-hub", element: <InterviewHubPage /> },
        { path: "/recruitment-reports", element: <RecruitmentReportsPage /> },
        { path: "/recruitment-reports/time-to-hire", element: <RecruitmentReportsPage /> },
        { path: "/recruitment-reports/sources", element: <RecruitmentReportsPage /> },
        { path: "/recruitment-reports/interviews", element: <RecruitmentReportsPage /> },
        { path: "/recruitment-reports/offers", element: <RecruitmentReportsPage /> },
        { path: "/recruitment-reports/pipeline", element: <RecruitmentReportsPage /> },
        { path: "/recruitment-reports/screening", element: <RecruitmentReportsPage /> },
        { path: "/recruiter-preferences", element: <RecruiterPreferencesPage /> },
        { path: "/preferred-companies", element: <Navigate to="/recruiter-preferences" replace /> },
        { path: "/announcements", element: <AnnouncementsPage /> },
        { path: "/my-leaves", element: <EmployeePortal /> },
        { path: "/attendance", element: <AttendancePage /> },
        { path: "/timesheet", element: <TimesheetPage />, guard: "timesheetEnabled" },
        { path: "/my-slips", element: <EmployeeSlipsView /> },
        { path: "/expenses", element: <ExpensesPage /> },
        { path: "/performance", element: <PerformancePage /> },
        ...PERFORMANCE_SUBROUTES_SELF(<PerformancePage />),
        { path: "/assets", element: <AssetsPage /> },
        { path: "/letters", element: <LettersPage /> },
        { path: "/esign", element: <EsignPage /> },
        ...ESIGN_SUBROUTES_SENDER(<EsignPage />),
        { path: "/esign/:requestId/:recipientId", element: <EsignSignPage /> },
        { path: "/helpdesk", element: <HelpdeskPage /> },
        { path: "/referrals", element: <ReferralsPage /> },
        { path: "/pulse", element: <PulsePage /> },
        { path: "/policies", element: <PoliciesPage /> },
        { path: "/exit", element: <ExitPage /> },
        // AC2/AC3 — the self-service report moved onto My Leaves; the old route
        // redirects there rather than 404ing.
        { path: "/reports", element: <Navigate to="/my-leaves" replace /> },
        { path: "/security", element: <SecurityPage /> },
        { path: "*", element: <NotFound /> },
      ],
    },
    // hr (was HRShell)
    hr: {
      nav: [
        { to: "/", label: "Dashboard", end: true },
        { section: "Recruitment" },
        { to: "/jobs", label: <NavBadge label="Jobs" count={summary?.open_jobs} /> },
        { to: "/candidates", label: <NavBadge label="Candidates" count={summary?.new_candidates} color="#7c3aed" /> },
        { to: "/talent-pool", label: "Talent Pool" },
        { to: "/offers", label: "Offers" },
        { to: "/interview-hub", label: "Interview Hub" },
        { to: "/screening", label: "AI Screening" },
        { to: "/recruitment-reports", label: "Reports" },
        { to: "/recruiter-preferences", label: "Recruiter Preferences" },
        { section: "People" },
        { to: "/leaves", label: <NavBadge label="Leaves" count={summary?.pending_leaves} /> },
        { to: "/my-leaves", label: "My Leaves" },
        { to: "/my-slips", label: "My Salary Slips" },
        { to: "/employees", label: "Employees" },
        { to: "/attendance", label: "Attendance" },
        { to: "/timesheet", label: "Timesheet", guard: "timesheetEnabled" },
        { to: "/people-settings", label: "People Settings", guard: "role:admin" },
        ...PERFORMANCE_NAV_SELF(),
        ...PERFORMANCE_NAV_MANAGE(),
        { to: "/org-chart", label: "Org Chart" },
        { to: "/expenses", label: "Reimbursements" },
        { to: "/assets", label: "Assets" },
        { to: "/letters", label: "Letters" },
        ...ESIGN_NAV_SENDER(),
        { to: "/helpdesk", label: "Helpdesk" },
        { to: "/announcements", label: "Announcements" },
        { to: "/referrals", label: "Referrals" },
        { to: "/pulse", label: "Pulse" },
        { to: "/policies", label: "Policies" },
        { to: "/exit", label: "Offboarding" },
        // Reports fold into the module that owns their data (spec
        // reports-v2-shell-and-people-analytics, Package 1). Payroll Reports is
        // listed here rather than appended to PAYROLL_NAV because that nav is
        // shared with the finance shell, and the report API is
        // require_leave_manager (admin/admin_assistant/hr) — finance would get
        // a visible link that 403s.
        { to: "/people-reports", label: "Reports" },
        // No Payroll Reports for hr: /payroll/* is the payroll module's route
        // namespace, and deps.py require_payroll_access is admin /
        // admin_assistant / finance — hr has no payroll module access, so
        // offering it here is nav that leads somewhere the module model
        // forbids (tests/test_module_role_access.py catches exactly this).
        // One section per purchasable module. Payroll, Finance and AI Expense
        // Capture are three separate entries in MODULE_KEYS, and a tenant can
        // own any one without the others — so filing all three under a single
        // "Finance" heading described the org chart rather than what the
        // workspace actually has.
        { section: "Payroll" },
        ...PAYROLL_NAV(),
        { section: "Finance" },
        { to: "/invoices", label: <NavBadge label="Invoices" count={summary?.overdue_invoices} color="#ef4444" />, guard: "legacyInvoicing" },
        { to: "/quotations", label: "Quotations" },
        { to: "/billing-notes", label: "Billing Notes" },
        { to: "/tax-invoices", label: "Tax Invoices" },
        { to: "/receipts", label: "Receipts" },
        { to: "/debit-credit-notes", label: "Credit / Debit Notes (Legacy)", guard: "legacyInvoicing" },
        { to: "/sales-credit-debit-notes", label: "Credit / Debit Notes" },
        { section: "AI Expense Capture" },
        ...AIEXP_NAV(),
        { to: "/products", label: "Products" },
        { to: "/vendors", label: "Vendors" },
        { to: "/vendor-bills", label: "Vendor Bills" },
        // Was a four-branch role ternary in HRShell. The generator flattened it
        // into four unguarded copies, so admin saw Workspace three times and
        // Settings twice. Restored as explicit role guards.
        //
        // Only admin, hr and unrecognised roles reach this config -- roleKeyFor
        // sends admin_assistant to its own -- so the original admin_assistant
        // branch is not reproduced here.
        { section: "System" },
        { to: "/settings", label: "Settings", end: true, guard: "role:admin" },
        { to: "/workspace", label: "Workspace", guard: "role:admin,hr" },
        { to: "/security", label: "Security" },
      ],
      routes: [
        { path: "/", element: <DashboardPage /> },
        { path: "/jobs", element: <JobsPage /> },
        { path: "/jobs/new", element: <NewJobPage /> },
        { path: "/jobs/:id", element: <JobDetailPage /> },
        { path: "/candidates", element: <CandidatesPage /> },
        { path: "/talent-pool", element: <TalentPoolPage /> },
        { path: "/offers", element: <OffersPageWrapper /> },
        { path: "/screening", element: <ScreeningPage /> },
        { path: "/interview-hub", element: <InterviewHubPage /> },
        { path: "/recruitment-reports", element: <RecruitmentReportsPage /> },
        { path: "/recruitment-reports/time-to-hire", element: <RecruitmentReportsPage /> },
        { path: "/recruitment-reports/sources", element: <RecruitmentReportsPage /> },
        { path: "/recruitment-reports/interviews", element: <RecruitmentReportsPage /> },
        { path: "/recruitment-reports/offers", element: <RecruitmentReportsPage /> },
        { path: "/recruitment-reports/pipeline", element: <RecruitmentReportsPage /> },
        { path: "/recruitment-reports/screening", element: <RecruitmentReportsPage /> },
        { path: "/recruiter-preferences", element: <RecruiterPreferencesPage /> },
        { path: "/preferred-companies", element: <Navigate to="/recruiter-preferences" replace /> },
        { path: "/leaves", element: <LeaveManagementPage /> },
        { path: "/my-leaves", element: <EmployeePortal /> },
        { path: "/my-slips", element: <EmployeeSlipsView /> },
        { path: "/employees", element: <EmployeesPage /> },
        { path: "/attendance", element: <AttendancePage /> },
        { path: "/timesheet", element: timesheetEnabled ? <TimesheetPage /> : <NotFound />, guard: "timesheetEnabled" },
        { path: "/people-settings", element: ["admin","admin_assistant"].includes(user?.role ?? "") ? <PeopleSettingsPage /> : <AccessDenied /> },
        { path: "/performance", element: <PerformancePage /> },
        ...PERFORMANCE_SUBROUTES_SELF(<PerformancePage />),
        ...PERFORMANCE_SUBROUTES_MANAGE(<PerformancePage />),
        { path: "/org-chart", element: <OrgChartPage /> },
        { path: "/expenses", element: <ExpensesPage /> },
        { path: "/assets", element: <AssetsPage /> },
        { path: "/letters", element: <LettersPage /> },
        { path: "/esign", element: <EsignPage /> },
        ...ESIGN_SUBROUTES_SENDER(<EsignPage />),
        { path: "/esign/:requestId/:recipientId", element: <EsignSignPage /> },
        { path: "/helpdesk", element: <HelpdeskPage /> },
        { path: "/referrals", element: <ReferralsPage /> },
        { path: "/pulse", element: <PulsePage /> },
        { path: "/announcements", element: <AnnouncementsPage /> },
        { path: "/policies", element: <PoliciesPage /> },
        { path: "/exit", element: <ExitPage /> },
        { path: "/people-reports", element: <PeopleReportsPage /> },
        { path: "/people-reports/movement", element: <PeopleReportsPage /> },
        { path: "/people-reports/attrition", element: <PeopleReportsPage /> },
        { path: "/people-reports/org", element: <PeopleReportsPage /> },
        { path: "/people-reports/leave", element: <PeopleReportsPage /> },
        { path: "/people-reports/liability", element: <PeopleReportsPage /> },
        { path: "/people-reports/absence", element: <PeopleReportsPage /> },
        // AC2 — /reports is retired but must not 404 for anyone holding a
        // bookmark or a link in an old email.
        { path: "/reports", element: <Navigate to="/people-reports" replace /> },
        { path: "/payroll", element: user?.role === "admin" ? <SalarySlipsPage /> : <AccessDenied />, guard: "role:admin" },
        ...PAYROLL_SUBROUTES(user?.role === "admin" ? <SalarySlipsPage /> : <AccessDenied />, "role:admin"),
        { path: "/invoices", element: user?.role === "admin" ? <InvoicesPage /> : <AccessDenied />, guard: "legacyInvoicing+role:admin" },
        { path: "/quotations", element: user?.role === "admin" ? <QuotationsPage /> : <AccessDenied />, guard: "role:admin" },
        { path: "/billing-notes", element: user?.role === "admin" ? <BillingNotesPage /> : <AccessDenied />, guard: "role:admin" },
        { path: "/tax-invoices", element: user?.role === "admin" ? <TaxInvoicesPage /> : <AccessDenied />, guard: "role:admin" },
        { path: "/receipts", element: user?.role === "admin" ? <ReceiptsPage /> : <AccessDenied />, guard: "role:admin" },
        { path: "/debit-credit-notes", element: user?.role === "admin" ? <DebitCreditNotesPage /> : <AccessDenied />, guard: "legacyInvoicing+role:admin" },
        { path: "/sales-credit-debit-notes", element: user?.role === "admin" ? <SalesCreditDebitNotesPage /> : <AccessDenied />, guard: "role:admin" },
        { path: "/vendors", element: user?.role === "admin" ? <VendorsPage /> : <AccessDenied />, guard: "role:admin" },
        { path: "/vendor-bills", element: user?.role === "admin" ? <VendorBillsPage /> : <AccessDenied />, guard: "role:admin" },
        { path: "/ai-expense-capture", element: user?.role === "admin" ? <AIExpenseCapturePage /> : <AccessDenied />, guard: "role:admin" },
        ...AIEXP_SUBROUTES(user?.role === "admin" ? <AIExpenseCapturePage /> : <AccessDenied />, "role:admin"),
        { path: "/products", element: user?.role === "admin" ? <ProductsPage /> : <AccessDenied />, guard: "role:admin" },
        { path: "/settings", element: ["admin","admin_assistant"].includes(user?.role ?? "") ? <SettingsPage /> : <AccessDenied /> },
        { path: "/workspace", element: ["admin","admin_assistant","hr"].includes(user?.role ?? "") ? <TenantPage /> : <AccessDenied /> },
        { path: "/security", element: <SecurityPage /> },
        { path: "*", element: <NotFound /> },
      ],
    },
  };
}
